import { pb } from "./pocketbase";
import type { RecordModel } from "pocketbase";

export interface AuthUser {
  id: string;
  email?: string; // Email is optional, can be added later
  name: string;
  password: string;
  avatar?: string;
  age?: number;
  riskTolerance?: "conservative" | "moderate" | "aggressive";
  investmentGoals?: string[];
  emergencyFundTarget?: number;
  retirementAge?: number;
  targetSavingsRate?: number;
  preferredInvestmentTypes?: string[];
  created: string;
  updated: string;
}

// Helper function to convert PocketBase RecordModel to AuthUser
const convertRecordToAuthUser = (record: RecordModel): AuthUser => {
  return {
    id: record.id,
    email: record.email,
    name: record.name,
    password: record.password || "", // PocketBase stores this as password
    avatar: record.avatar,
    age: record.age,
    riskTolerance: record.riskTolerance,
    investmentGoals: record.investmentGoals,
    emergencyFundTarget: record.emergencyFundTarget,
    retirementAge: record.retirementAge,
    targetSavingsRate: record.targetSavingsRate,
    preferredInvestmentTypes: record.preferredInvestmentTypes,
    created: record.created,
    updated: record.updated,
  };
};

export const authService = {
  // Sign up with email and password
  async signUp(
    email: string,
    password: string,
    name: string
  ): Promise<AuthUser> {
    // Validate password is at least 8 characters
    if (password.length < 8) {
      throw new Error("Password must be at least 8 characters");
    }

    const data = {
      email,
      password: password, // PocketBase will hash this
      passwordConfirm: password,
      name,
    };

    try {
      const record = await pb.collection("users").create(data);
      return convertRecordToAuthUser(record);
    } catch (error: any) {
      console.error("Sign up error:", error);

      // Check for specific PocketBase error codes
      if (error.status === 400) {
        // Validation errors
        if (error.data?.message?.includes("Failed to validate")) {
          throw new Error("Please check your email and password format.");
        } else if (error.data?.message?.includes("password")) {
          throw new Error("Password must be at least 8 characters.");
        }
      } else if (error.status === 409) {
        // Email already exists
        throw new Error(
          "An account with this email already exists. Please sign in instead."
        );
      } else if (error.data?.message) {
        throw new Error(error.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error(
          "Failed to create account. Please check your connection and try again."
        );
      }

      // This should never be reached, but TypeScript needs it
      throw new Error("An unexpected error occurred during sign up.");
    }
  },

  // Sign in with email and password
  async signIn(email: string, password: string): Promise<AuthUser> {
    // Validate password is at least 8 characters
    if (password.length < 8) {
      throw new Error("Password must be at least 8 characters");
    }

    try {
      const authData = await pb
        .collection("users")
        .authWithPassword(email, password);
      return convertRecordToAuthUser(authData.record);
    } catch (error: any) {
      console.error("Sign in error:", error);

      // Check for specific PocketBase error codes
      if (error.status === 400) {
        // Invalid credentials - could be wrong email or password
        if (error.data?.message?.includes("Invalid login credentials")) {
          throw new Error("Invalid email or password. Please try again.");
        }
      } else if (error.status === 404) {
        // Record not found - account doesn't exist
        throw new Error(
          "This account does not exist, please try a different account"
        );
      } else if (error.data?.message) {
        throw new Error(error.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error("Invalid email or password. Please try again.");
      }

      // This should never be reached, but TypeScript needs it
      throw new Error("An unexpected error occurred during sign in.");
    }
  },

  // Sign out
  signOut(): void {
    pb.authStore.clear();
  },

  // Get current authenticated user
  getCurrentUser(): AuthUser | null {
    if (!pb.authStore.model) return null;
    return convertRecordToAuthUser(pb.authStore.model);
  },

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return pb.authStore.isValid;
  },

  // Update user profile
  async updateProfile(
    userId: string,
    updates: Partial<AuthUser>
  ): Promise<AuthUser> {
    try {
      const record = await pb.collection("users").update(userId, updates);
      return convertRecordToAuthUser(record);
    } catch (error: any) {
      console.error("Profile update error:", error);
      
      // Handle specific error cases
      if (error.status === 400) {
        if (error.data?.message?.includes("email")) {
          throw new Error("Please enter a valid email address.");
        }
      } else if (error.status === 409) {
        throw new Error("This email is already in use. Please try a different email.");
      } else if (error.data?.message) {
        throw new Error(error.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error("Failed to update profile. Please try again.");
      }
      
      throw new Error("An unexpected error occurred while updating your profile.");
    }
  },

  // Change password
  async changePassword(
    userId: string,
    currentPassword: string,
    newPassword: string
  ): Promise<void> {
    // Validate new password is at least 8 characters
    if (newPassword.length < 8) {
      throw new Error("Password must be at least 8 characters");
    }

    // First verify current password
    const currentUser = this.getCurrentUser();
    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    // Update password in PocketBase
    await pb.collection("users").update(userId, {
      password: newPassword,
      passwordConfirm: newPassword,
    });
  },
};
